/**
 * Secure random number generation utility using cryptographically secure PRNG
 */

/**
 * Generates a secure random float between 0 (inclusive) and 1 (exclusive)
 * @returns A secure random number between 0 and 1
 */
export function secureRandom(): number {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  // Convert to 0-1 range
  return array[0] / (0xffffffff + 1);
}

/**
 * Generates a secure random integer between min (inclusive) and max (inclusive)
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @returns A secure random integer
 */
export function secureRandomInt(min: number, max: number): number {
  const range = max - min + 1;
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return Math.floor((array[0] / (0xffffffff + 1)) * range) + min;
}

/**
 * Generates a secure random boolean with optional probability
 * @param probability - Probability of returning true (0-1), defaults to 0.5
 * @returns A secure random boolean
 */
export function secureRandomBoolean(probability: number = 0.5): boolean {
  return secureRandom() < probability;
}

/**
 * Generates a secure random string for IDs
 * @param length - Length of the string to generate
 * @returns A secure random string
 */
export function secureRandomString(length: number = 8): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);

  for (let i = 0; i < length; i++) {
    result += chars[array[i] % chars.length];
  }

  return result;
}
